import React, { useCallback, useState } from 'react';
import { SelectionArea } from '../types';

interface SelectionToolProps {
  width: number;
  height: number;
  onSelectionComplete: (selection: SelectionArea, layerId: string) => void;
  selectedLayerId?: string;
}

const SelectionTool: React.FC<SelectionToolProps> = ({
  onSelectionComplete,
  selectedLayerId,
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectionRect, setSelectionRect] = useState<SelectionArea | null>(
    null
  );
  const startPosRef = React.useRef<{ x: number; y: number } | null>(null);

  // マウスイベントの座標を取得
  const getMousePosition = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    return pos;
  }, []);

  // 選択開始
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const pos = getMousePosition(e);
      startPosRef.current = pos;
      setIsDrawing(true);
      setSelectionRect(null);
    },
    [getMousePosition]
  );

  // 選択範囲の更新
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDrawing || !startPosRef.current) return;

      const currentPos = getMousePosition(e);
      const startPos = startPosRef.current;

      const newRect = {
        x: Math.min(startPos.x, currentPos.x),
        y: Math.min(startPos.y, currentPos.y),
        width: Math.abs(currentPos.x - startPos.x),
        height: Math.abs(currentPos.y - startPos.y),
      };

      setSelectionRect(newRect);
    },
    [isDrawing, getMousePosition]
  );

  // 選択完了
  const handleMouseUp = useCallback(() => {
    if (
      !isDrawing ||
      !selectedLayerId ||
      !startPosRef.current ||
      !selectionRect
    )
      return;

    onSelectionComplete(selectionRect, selectedLayerId);
    setIsDrawing(false);
    startPosRef.current = null;
  }, [isDrawing, selectedLayerId, selectionRect, onSelectionComplete]);

  return (
    <div
      className="absolute inset-0"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{ cursor: 'crosshair' }}
    >
      {selectionRect && (
        <div
          className="absolute border-2 border-blue-500 bg-blue-200 bg-opacity-20"
          style={{
            left: selectionRect.x,
            top: selectionRect.y,
            width: selectionRect.width,
            height: selectionRect.height,
          }}
        />
      )}
    </div>
  );
};

export default SelectionTool;
